/**
 * Role Switcher Component
 * Dropdown to switch between available roles
 */

import React, { useState, useRef, useEffect } from 'react';
import { useRole } from '../contexts/RoleContext';
import './RoleSwitcher.css';

export default function RoleSwitcher() {
  const { currentRole, availableRoles, switchRole } = useRole();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Don't show if user has no roles
  if (availableRoles.length === 0) {
    return null;
  }

  const currentRoleData = availableRoles.find(r => r.id === currentRole) || availableRoles[0];
  const canSwitch = availableRoles.length > 1;

  function handleRoleSelect(roleId) {
    switchRole(roleId);
    setIsOpen(false);
  }

  return (
    <div className="role-switcher" ref={dropdownRef}>
      <button
        className="role-switcher-button"
        onClick={() => canSwitch && setIsOpen(!isOpen)}
        aria-label={canSwitch ? 'Switch role' : 'Current role'}
        aria-haspopup={canSwitch ? 'listbox' : undefined}
        aria-expanded={canSwitch ? isOpen : undefined}
      >
        <span className="role-icon">{currentRoleData.icon}</span>
        <span className="role-name">{currentRoleData.name}</span>
        {canSwitch && <span className="role-arrow">{isOpen ? '▲' : '▼'}</span>}
      </button>

      {canSwitch && isOpen && (
        <div className="role-dropdown" role="listbox">
          {availableRoles.map(role => (
            <button
              key={role.id}
              role="option"
              aria-selected={role.id === currentRole}
              className={`role-option ${role.id === currentRole ? 'active' : ''}`}
              onClick={() => handleRoleSelect(role.id)}
            >
              <span className="role-icon">{role.icon}</span>
              <span className="role-name">{role.name}</span>
              {role.id === currentRole && <span className="role-check">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

